/**
 * `W5-T09` §4.9 — one document on screen: the provisional banner, then `documentRows`' groups (the
 * server's `documentLines`, row for row), then **Download PDF**, a plain link to the API's PDF in the
 * page's language. AC 36.
 */
import { type ReactElement } from 'react';
import { useTranslation } from 'react-i18next';
import { type MoneyDocument } from '@marketplace/contracts';
import { documentPdfUrl } from './documents.js';
import { documentRows, type DocumentLocale, type DocumentRow } from './rows.js';

function Row({ row }: { row: DocumentRow }): ReactElement {
  if (row.type === 'heading') return <h2 data-key={row.key}>{row.text}</h2>;
  if (row.type === 'note') return <p data-key={row.key}>{row.text}</p>;
  return (
    <div data-key={row.key} className="mp-row">
      <dt>{row.label}</dt>
      <dd>{row.value}</dd>
    </div>
  );
}

export function DocumentView({
  doc,
  locale,
}: {
  doc: MoneyDocument;
  locale: DocumentLocale;
}): ReactElement {
  const { t } = useTranslation();
  const { banner, groups } = documentRows(doc, locale);
  return (
    <article data-testid="document">
      <p className="mp-lead" role="note" data-testid="documents-banner">
        <strong>{banner}</strong>
      </p>
      {groups.map((group, index) => (
        <section key={group[0]?.key ?? index} className="mp-section">
          <dl>
            {group.map((row) => (
              <Row key={row.key} row={row} />
            ))}
          </dl>
        </section>
      ))}
      <p>
        <a href={documentPdfUrl(doc.id, locale)} download>
          {t('documents.download')}
        </a>
      </p>
    </article>
  );
}
